import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import {
  Plus,
  Search,
  Filter,
  Receipt,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MoreHorizontal,
  Check,
  X,
  CreditCard,
  FileText,
  DollarSign,
  Clock,
  AlertCircle,
  Trash2,
  Building2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { ExpenseModal } from '@/components/travel/expenses/expense-modal';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { PaymentAccountSelect } from '@/components/travel/accounting/payment-account-select';
import { useSessionState } from '@/hooks/use-session-state';

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  REJECTED: 'bg-red-100 text-red-700',
  PAID: 'bg-green-100 text-green-700',
};

const statusLabelKeys: Record<string, string> = {
  PENDING: 'status.pending',
  APPROVED: 'status.approved',
  REJECTED: 'status.rejected',
  PAID: 'status.paid',
};

const categoryLabelKeys: Record<string, string> = {
  SUPPLIER_PAYMENT: 'category.supplierPayment',
  SALARY: 'category.salary',
  RENT: 'category.rent',
  UTILITIES: 'category.utilities',
  MARKETING: 'category.marketing',
  TRAVEL: 'category.travel',
  OFFICE_SUPPLIES: 'category.officeSupplies',
  INSURANCE: 'category.insurance',
  TAXES: 'category.taxes',
  OTHER: 'category.other',
};

type ExpenseStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAID';
type ExpenseCategory =
  | 'SUPPLIER_PAYMENT'
  | 'SALARY'
  | 'RENT'
  | 'UTILITIES'
  | 'MARKETING'
  | 'TRAVEL'
  | 'OFFICE_SUPPLIES'
  | 'INSURANCE'
  | 'TAXES'
  | 'OTHER';
type PaymentMethod =
  | 'CREDIT_CARD'
  | 'DEBIT_CARD'
  | 'BANK_TRANSFER'
  | 'PAYPAL'
  | 'CASH'
  | 'CHEQUE'
  | 'OTHER';

export function ExpensesPage() {
  const { t, i18n } = useTranslation('expenses');
  const { t: tCommon } = useTranslation('common');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const navigate = useNavigate();

  const canCreate = usePermission('EXPENSES', 'CREATE');
  const canEdit = usePermission('EXPENSES', 'EDIT');
  const canDelete = usePermission('EXPENSES', 'DELETE');

  const [debouncedSearch, setDebouncedSearch] = useSessionState<string>(
    'travel:expenses:search',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useSessionState<ExpenseStatus | undefined>(
    'travel:expenses:statusFilter',
    undefined
  );
  const [categoryFilter, setCategoryFilter] = useSessionState<ExpenseCategory | undefined>(
    'travel:expenses:categoryFilter',
    undefined
  );
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [editExpenseId, setEditExpenseId] = useState<string | undefined>();

  // Dialog states
  const [approveDialog, setApproveDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });
  const [rejectDialog, setRejectDialog] = useState<{
    open: boolean;
    id: string | null;
    reason: string;
  }>({ open: false, id: null, reason: '' });
  const [markPaidDialog, setMarkPaidDialog] = useState<{
    open: boolean;
    id: string | null;
    method: PaymentMethod;
    ref: string;
    accountId?: string;
  }>({
    open: false,
    id: null,
    method: 'BANK_TRANSFER',
    ref: '',
  });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });

  const limit = 10;

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const { data, isLoading, error } = trpc.expense.list.useQuery({
    page,
    limit,
    search: debouncedSearch || undefined,
    status: statusFilter,
    category: categoryFilter,
  });

  const { data: stats } = trpc.expense.stats.useQuery({});

  const utils = trpc.useUtils();

  const approveMutation = trpc.expense.approve.useMutation({
    onSuccess: () => {
      utils.expense.list.invalidate();
      utils.expense.stats.invalidate();
      showSuccessToast(t('toasts.approved'));
      setApproveDialog({ open: false, id: null });
    },
    onError: (error) => {
      showErrorToast(error.message || t('toasts.approveFailed'));
    },
  });

  const rejectMutation = trpc.expense.reject.useMutation({
    onSuccess: () => {
      utils.expense.list.invalidate();
      utils.expense.stats.invalidate();
      showSuccessToast(t('toasts.rejected'));
      setRejectDialog({ open: false, id: null, reason: '' });
    },
    onError: (error) => {
      showErrorToast(error.message || t('toasts.rejectFailed'));
    },
  });

  const markPaidMutation = trpc.expense.markPaid.useMutation({
    onSuccess: () => {
      utils.expense.list.invalidate();
      utils.expense.stats.invalidate();
      showSuccessToast(t('toasts.markedPaid'));
      setMarkPaidDialog({ open: false, id: null, method: 'BANK_TRANSFER', ref: '' });
    },
    onError: (error) => {
      showErrorToast(error.message || t('toasts.markPaidFailed'));
    },
  });

  const deleteMutation = trpc.expense.delete.useMutation({
    onSuccess: () => {
      utils.expense.list.invalidate();
      utils.expense.stats.invalidate();
      showSuccessToast(t('toasts.deleted'));
      setDeleteDialog({ open: false, id: null });
    },
    onError: (error) => {
      showErrorToast(error.message || t('toasts.deleteFailed'));
    },
  });

  const expenses = data?.expenses ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 10, total: 0, totalPages: 0 };

  const handleExpenseCreated = (expenseId: string) => {
    navigate(`/travel/expenses/${expenseId}`);
  };

  const handleEditExpense = (id: string) => {
    setEditExpenseId(id);
    setIsExpenseModalOpen(true);
  };

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">{t('title')}</h1>
          <p className="text-muted-foreground text-sm">
            {t('subtitle')}
            {pagination.total > 0 && ` (${pagination.total} ${tCommon('total')})`}
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={() => {
              setEditExpenseId(undefined);
              setIsExpenseModalOpen(true);
            }}
            className="h-8"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('createExpense')}
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-3 md:grid-cols-4">
          <div className="border-border bg-card border p-3">
            <div className="flex items-center gap-3">
              <div className="bg-red-100 p-2">
                <DollarSign className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <p className="text-lg font-semibold">{formatCurrency(stats.totalAmount, 'TRY')}</p>
                <p className="text-muted-foreground text-xs">{t('stats.totalExpenses')}</p>
              </div>
            </div>
          </div>
          <div className="border-border bg-card border p-3">
            <div className="flex items-center gap-3">
              <div className="bg-yellow-100 p-2">
                <Clock className="h-4 w-4 text-yellow-600" />
              </div>
              <div>
                <p className="text-lg font-semibold">{stats.byStatus.pending}</p>
                <p className="text-muted-foreground text-xs">{t('stats.pendingApproval')}</p>
              </div>
            </div>
          </div>
          <div className="border-border bg-card border p-3">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2">
                <AlertCircle className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-lg font-semibold">{stats.byStatus.approved}</p>
                <p className="text-muted-foreground text-xs">{t('stats.approvedUnpaid')}</p>
              </div>
            </div>
          </div>
          <div className="border-border bg-card border p-3">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2">
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="text-lg font-semibold">{stats.byStatus.paid}</p>
                <p className="text-muted-foreground text-xs">{t('stats.paidThisMonth')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats Row */}
      {stats && (
        <div className="flex gap-3 overflow-x-auto pb-2">
          <div className="border-border bg-card flex items-center gap-2 border px-3 py-1.5">
            <span className="h-2 w-2 bg-yellow-400" />
            <span className="text-xs">
              {t('stats.pending')}: {stats.byStatus.pending}
            </span>
          </div>
          <div className="border-border bg-card flex items-center gap-2 border px-3 py-1.5">
            <span className="h-2 w-2 bg-blue-400" />
            <span className="text-xs">
              {t('stats.approved')}: {stats.byStatus.approved}
            </span>
          </div>
          <div className="border-border bg-card flex items-center gap-2 border px-3 py-1.5">
            <span className="h-2 w-2 bg-green-400" />
            <span className="text-xs">
              {t('stats.paid')}: {stats.byStatus.paid}
            </span>
          </div>
          <div className="border-border bg-card flex items-center gap-2 border px-3 py-1.5">
            <span className="h-2 w-2 bg-red-400" />
            <span className="text-xs">
              {t('stats.rejected')}: {stats.byStatus.rejected}
            </span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border py-1.5 pl-10 pr-3 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <select
          value={statusFilter ?? ''}
          onChange={(e) => {
            setStatusFilter((e.target.value as ExpenseStatus) || undefined);
            setPage(1);
          }}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1"
        >
          <option value="">{t('allStatuses')}</option>
          <option value="PENDING">{t('status.pending')}</option>
          <option value="APPROVED">{t('status.approved')}</option>
          <option value="PAID">{t('status.paid')}</option>
          <option value="REJECTED">{t('status.rejected')}</option>
        </select>
        <select
          value={categoryFilter ?? ''}
          onChange={(e) => {
            setCategoryFilter((e.target.value as ExpenseCategory) || undefined);
            setPage(1);
          }}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1"
        >
          <option value="">{t('allCategories')}</option>
          {Object.entries(categoryLabelKeys).map(([value, labelKey]) => (
            <option key={value} value={value}>
              {t(labelKey)}
            </option>
          ))}
        </select>
        <Button variant="outline" className="h-8">
          <Filter className="mr-2 h-4 w-4" />
          {t('moreFilters')}
        </Button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-6 w-6 animate-spin" />
          <span className="text-muted-foreground ml-2 text-sm">{t('loadingExpenses')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-3 text-red-700">
          <p className="text-sm font-medium">{t('errorLoading')}</p>
          <p className="text-xs">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && expenses.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Receipt className="text-muted-foreground/50 h-10 w-10" />
          <h3 className="mt-4 text-sm font-medium">{t('noExpensesFound')}</h3>
          <p className="text-muted-foreground mt-1 text-xs">
            {debouncedSearch || statusFilter || categoryFilter
              ? t('adjustFilters')
              : t('expensesWillAppear')}
          </p>
          {!debouncedSearch && !statusFilter && !categoryFilter && canCreate && (
            <Button className="mt-4 h-8" onClick={() => setIsExpenseModalOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('createExpense')}
            </Button>
          )}
        </div>
      )}

      {/* Expenses Table */}
      {!isLoading && !error && expenses.length > 0 && (
        <>
          <div className="border-border bg-card overflow-hidden border">
            <table className="w-full">
              <thead className="bg-muted/50 border-border border-b">
                <tr>
                  <th className="text-muted-foreground px-3 py-2 text-left text-xs font-medium uppercase tracking-wider">
                    {t('table.expenseNumber')}
                  </th>
                  <th className="text-muted-foreground px-3 py-2 text-left text-xs font-medium uppercase tracking-wider">
                    {t('table.date')}
                  </th>
                  <th className="text-muted-foreground px-3 py-2 text-left text-xs font-medium uppercase tracking-wider">
                    {t('table.vendor')}
                  </th>
                  <th className="text-muted-foreground px-3 py-2 text-left text-xs font-medium uppercase tracking-wider">
                    {t('table.category')}
                  </th>
                  <th className="text-muted-foreground px-3 py-2 text-left text-xs font-medium uppercase tracking-wider">
                    {t('table.status')}
                  </th>
                  <th className="text-muted-foreground px-3 py-2 text-right text-xs font-medium uppercase tracking-wider">
                    {t('table.amount')}
                  </th>
                  <th className="text-muted-foreground px-3 py-2 text-right text-xs font-medium uppercase tracking-wider">
                    {t('table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {expenses.map((expense) => (
                  <tr key={expense.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-3 py-2">
                      <Link
                        to={`/travel/expenses/${expense.id}`}
                        className="text-primary text-sm font-medium hover:underline"
                      >
                        {expense.expenseNumber || '-'}
                      </Link>
                      {expense.booking && (
                        <p className="text-muted-foreground text-xs">
                          {t('table.booking')}: {expense.booking.bookingReference}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {new Date(expense.expenseDate).toLocaleDateString(dateLocale)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {expense.partner ? (
                          <>
                            <Building2 className="text-muted-foreground h-3.5 w-3.5" />
                            <span className="text-sm">{expense.partner.companyName}</span>
                          </>
                        ) : (
                          <span className="text-sm">{expense.vendorName || '-'}</span>
                        )}
                      </div>
                      <p className="text-muted-foreground max-w-[200px] truncate text-xs">
                        {expense.description}
                      </p>
                    </td>
                    <td className="px-3 py-2">
                      <span className="bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                        {t(categoryLabelKeys[expense.category] || 'category.other')}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`px-2 py-0.5 text-xs font-medium ${
                          statusColors[expense.status] || 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {t(statusLabelKeys[expense.status] || 'status.pending')}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <p className="text-sm font-medium">
                        {formatCurrency(expense.amount, expense.currency)}
                      </p>
                      {expense.taxAmount > 0 && (
                        <p className="text-muted-foreground text-xs">
                          {t('table.taxAmount')}:{' '}
                          {formatCurrency(expense.taxAmount, expense.currency)}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/travel/expenses/${expense.id}`}>
                              <FileText className="mr-2 h-4 w-4" />
                              {t('actions.viewDetails')}
                            </Link>
                          </DropdownMenuItem>
                          {expense.status === 'PENDING' && (
                            <>
                              {canEdit && (
                                <DropdownMenuItem onClick={() => handleEditExpense(expense.id)}>
                                  <FileText className="mr-2 h-4 w-4" />
                                  {t('actions.edit')}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setApproveDialog({ open: true, id: expense.id })}
                              >
                                <Check className="mr-2 h-4 w-4 text-green-600" />
                                {t('actions.approve')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  setRejectDialog({ open: true, id: expense.id, reason: '' })
                                }
                              >
                                <X className="mr-2 h-4 w-4 text-red-600" />
                                {t('actions.reject')}
                              </DropdownMenuItem>
                              {canDelete && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => setDeleteDialog({ open: true, id: expense.id })}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    {t('actions.delete')}
                                  </DropdownMenuItem>
                                </>
                              )}
                            </>
                          )}
                          {expense.status === 'APPROVED' && (
                            <DropdownMenuItem
                              onClick={() =>
                                setMarkPaidDialog({
                                  open: true,
                                  id: expense.id,
                                  method: 'BANK_TRANSFER',
                                  ref: '',
                                })
                              }
                            >
                              <CreditCard className="mr-2 h-4 w-4" />
                              {t('actions.markAsPaid')}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-xs">
                {t('pagination.showing', {
                  from: (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(pagination.page * pagination.limit, pagination.total),
                  total: pagination.total,
                })}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                  className="h-7"
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t('pagination.previous')}
                </Button>
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === pagination.page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        className="h-7 w-7 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="h-7"
                >
                  {t('pagination.next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Expense Modal */}
      {isExpenseModalOpen && (
        <ExpenseModal
          open={isExpenseModalOpen}
          onOpenChange={setIsExpenseModalOpen}
          onSuccess={handleExpenseCreated}
          editId={editExpenseId}
        />
      )}

      {/* Approve Dialog */}
      <Dialog
        open={approveDialog.open}
        onOpenChange={(open) => setApproveDialog({ ...approveDialog, open })}
      >
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="text-base">{t('dialogs.approveTitle')}</DialogTitle>
            <DialogDescription className="text-sm">{t('dialogs.approveMessage')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              className="h-8"
              onClick={() => setApproveDialog({ open: false, id: null })}
            >
              {tCommon('cancel')}
            </Button>
            <Button
              className="h-8"
              onClick={() => approveDialog.id && approveMutation.mutate({ id: approveDialog.id })}
              disabled={approveMutation.isPending}
            >
              {approveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('actions.approve')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialog.open}
        onOpenChange={(open) => setRejectDialog({ ...rejectDialog, open })}
      >
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="text-base">{t('dialogs.rejectTitle')}</DialogTitle>
            <DialogDescription className="text-sm">{t('dialogs.rejectMessage')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div className="space-y-1.5">
              <Label className="text-sm">{t('dialogs.rejectReason')}</Label>
              <Textarea
                value={rejectDialog.reason}
                onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
                placeholder={t('dialogs.rejectPlaceholder')}
                rows={3}
                className="border-border focus:border-foreground focus:ring-foreground/20 rounded-none focus:ring-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="h-8"
              onClick={() => setRejectDialog({ open: false, id: null, reason: '' })}
            >
              {tCommon('cancel')}
            </Button>
            <Button
              variant="destructive"
              className="h-8"
              onClick={() =>
                rejectDialog.id &&
                rejectMutation.mutate({ id: rejectDialog.id, reason: rejectDialog.reason })
              }
              disabled={rejectMutation.isPending}
            >
              {rejectMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('actions.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog
        open={markPaidDialog.open}
        onOpenChange={(open) => setMarkPaidDialog({ ...markPaidDialog, open })}
      >
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="text-base">{t('dialogs.markPaidTitle')}</DialogTitle>
            <DialogDescription className="text-sm">
              {t('dialogs.markPaidMessage')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div className="space-y-1.5">
              <Label className="text-sm">{t('dialogs.paymentMethod')}</Label>
              <Combobox
                options={[
                  { value: 'BANK_TRANSFER', label: t('paymentMethods.bankTransfer') },
                  { value: 'CREDIT_CARD', label: t('paymentMethods.creditCard') },
                  { value: 'DEBIT_CARD', label: t('paymentMethods.debitCard') },
                  { value: 'CASH', label: t('paymentMethods.cash') },
                  { value: 'CHEQUE', label: t('paymentMethods.cheque') },
                  { value: 'PAYPAL', label: t('paymentMethods.paypal') },
                  { value: 'OTHER', label: t('paymentMethods.other') },
                ]}
                value={markPaidDialog.method}
                onValueChange={(value) =>
                  setMarkPaidDialog({ ...markPaidDialog, method: value as PaymentMethod })
                }
                placeholder={t('dialogs.selectMethod')}
                searchPlaceholder={tCommon('search')}
              />
            </div>
            <PaymentAccountSelect
              value={markPaidDialog.accountId}
              onChange={(v) => setMarkPaidDialog({ ...markPaidDialog, accountId: v })}
            />
            <div className="space-y-1.5">
              <Label className="text-sm">{t('dialogs.paymentRef')}</Label>
              <Input
                value={markPaidDialog.ref}
                onChange={(e) => setMarkPaidDialog({ ...markPaidDialog, ref: e.target.value })}
                placeholder={t('dialogs.paymentRefPlaceholder')}
                className="border-border focus:border-foreground focus:ring-foreground/20 h-8 rounded-none focus:ring-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="h-8"
              onClick={() =>
                setMarkPaidDialog({ open: false, id: null, method: 'BANK_TRANSFER', ref: '' })
              }
            >
              {tCommon('cancel')}
            </Button>
            <Button
              className="h-8"
              onClick={() =>
                markPaidDialog.id &&
                markPaidMutation.mutate({
                  id: markPaidDialog.id,
                  paymentMethod: markPaidDialog.method,
                  paymentRef: markPaidDialog.ref || undefined,
                  accountId: markPaidDialog.accountId,
                })
              }
              disabled={markPaidMutation.isPending}
            >
              {markPaidMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('actions.markAsPaid')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}
      >
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="text-base">{t('dialogs.deleteTitle')}</DialogTitle>
            <DialogDescription className="text-sm">{t('dialogs.deleteMessage')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              className="h-8"
              onClick={() => setDeleteDialog({ open: false, id: null })}
            >
              {tCommon('cancel')}
            </Button>
            <Button
              variant="destructive"
              className="h-8"
              onClick={() => deleteDialog.id && deleteMutation.mutate(deleteDialog.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('actions.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
